/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: MultiSelectButton.tsx
 🗓️ Created: 2025-01-06
 🧩 Description:
    Trigger button component for multi-select modal with
    selected items display and modern styling.

 🔧 Notes:
    - Shows selected items count and preview
    - Integrates with MultiSelectModal
    - Supports disabled state and loading
    - Responsive design for different screen sizes

 🔁 Last Updated: 2025-01-06 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { motion } from 'framer-motion';
import { Plus, ListChecks } from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';

interface SelectableItem {
  id: string;
  name: string;
  description?: string;
  slug?: string;
}

interface MultiSelectButtonProps {
  /** All available items */
  items: SelectableItem[];
  /** Currently selected item IDs */
  selectedIds: string[];
  /** Button click handler */
  onClick: () => void;
  /** Button label */
  label: string;
  /** Whether to hide the label (useful when label is provided externally) */
  hideLabel?: boolean;
  /** Placeholder text when no items selected */
  placeholder?: string;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Whether button is loading */
  loading?: boolean;
  /** Icon for the button */
  icon?: React.ComponentType<any>;
  /** Maximum number of items to show in preview */
  maxPreviewItems?: number;
  /** Custom className */
  className?: string;
}

export const MultiSelectButton: React.FC<MultiSelectButtonProps> = ({
  items,
  selectedIds,
  onClick,
  label,
  hideLabel = false,
  placeholder = 'Select items...',
  disabled = false,
  loading = false,
  icon: Icon = ListChecks,
  maxPreviewItems = 2,
  className = ''
}) => {
  // Get selected items
  const selectedItems = items.filter(item => selectedIds.includes(item.id));
  
  // Calculate percentage
  const percentage = items.length > 0 ? Math.round((selectedItems.length / items.length) * 100) : 0;
  
  // Create preview text (single line, truncated)
  const getPreviewText = () => {
    if (selectedItems.length === 0) {
      return placeholder;
    }
    
    const previewText = selectedItems
      .slice(0, maxPreviewItems)
      .map(item => item.name)
      .join(', ');
    
    if (selectedItems.length > maxPreviewItems) {
      return `${previewText}...`;
    }
    
    return previewText;
  };

  return (
    <div className={`${hideLabel ? '' : 'space-y-1'} ${className}`}>
      {/* Label */}
      {!hideLabel && (
        <label className="block text-sm font-medium text-foreground">
          {label}
        </label>
      )}

      {/* Button - Single line, no expansion */}
      <Button
        type="button"
        variant="outline"
        onClick={onClick}
        disabled={disabled || loading}
        className="w-full justify-between h-10 px-3 py-2"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          
          <span 
            className={`text-sm truncate ${
              selectedItems.length === 0 
                ? 'text-muted-foreground' 
                : 'text-foreground'
            }`}
            title={selectedItems.length > 0 ? selectedItems.map(item => item.name).join(', ') : placeholder}
          >
            {getPreviewText()}
          </span>
        </div>

        <Plus className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
      </Button>

      {/* Subtitle with count and percentage */}
      <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
        <span>
          {selectedItems.length} of {items.length} selected
        </span>
        <span>
          {percentage}% selected
        </span>
      </div>
    </div>
  );
};

export default MultiSelectButton;
